import React, {
   CSSProperties,
   FocusEvent,
   forwardRef, PropsWithChildren, ReactElement,
   ReactNode,
   Ref, RefAttributes, useCallback, useContext, useEffect, useImperativeHandle,
   useRef
} from 'react';
import { convertSize } from '../../../../utils/StyleUtils';
import {
   CustomTagProps,
   DefaultValue, LabeledValue,
   OptionsType,
   Select,
   SelectRefProps, SingleType
} from '../../../controls/select';
import { FormContext, generateFormFieldId } from '../../form';

/**
 * {@docCategory SelectInput}
 */
export interface SelectInputProps<ValueType extends DefaultValue = DefaultValue> {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: ValueType;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * Select options. Use this property to get better performance than jsx
    * child definitions.
    */
   options?: OptionsType;

   /**
    * Config popup height
    */
   listHeight?: number;

   /**
    * Config popup list item height
    */
   listItemHeight?: number;

   /**
    * The inline style of dropdown menu
    */
   dropdownStyle?: CSSProperties;

   /**
    * The className of dropdown menu
    */
   dropdownClassName?: string;

   /**
    * Determine whether the dropdown menu and the select input are the same width.
    * Default set min-width same as input. Will ignore when value less than select
    * width. false will disable virtual scroll
    * @defaultvalue true
    */
   dropdownMatchSelectWidth?: boolean | number;

   /**
    * Parent Node which the selector should be rendered to. Default to body. When position issues
    * happen, try to modify it into scrollable content and position it relative
    * @param triggerNode - The trigger node
    * @defaultvalue () =\> document.body;
    */
   getPopupContainer?: (triggerNode: unknown) => HTMLElement;

   /**
    * Indicate loading state
    * @defaultvalue false
    */
   loading?: boolean;

   /**
    * Whether active first option by default
    * @defaultvalue true
    */
   defaultActiveFirstOption?: boolean;

   /**
    * Specify content to show when no result matches
    * @defaultvalue 'Not Found'
    */
   notFoundContent?: ReactNode;

   /**
    * Which prop value of option will render as content of the select
    */
   optionLabelProp?: string;

   /**
    * Which prop value of option will be used for filter if filterOption is true. If options
    * is set, it should be set to label
    */
   optionFilterProp?: string;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Whether or not to show the search feature
    */
   showSearch?: boolean;

   /**
    * Set the mode of Select
    */
   mode?: 'multiple' | 'tags';

   /**
    * Separator used to tokenize on tag and multiple mode
    */
   tokenSeparators?: string[];

   /**
    * Customize tag render
    * @param props - The custom tag props
    */
   tagRender?: (props: CustomTagProps) => React.ReactElement;

   /**
    * Max tag text length to show
    */
   maxTagTextLength?: number;

   /**
    * Max tag count to show.
    */
   maxTagCount?: number;

   /**
    * Placeholder for not showing tags
    */
   maxTagPlaceholder?: React.ReactNode | ((omittedValues: LabeledValue[]) => React.ReactNode);

   /**
    * If true, filter options by input, if function, filter options against it. The function will
    * receive two arguments, inputValue and option, if the function returns true, the option will
    * be included in the filtered set; Otherwise, it will be excluded
    */
   filterOption?: boolean | ((inputValue: string, option?: OptionsType[number]) => boolean);

   /**
    * Sort function for search options sorting, see Array.sort's compareFunction
    * @param optionA - The first option to compare
    * @param optionB - The second option to compare
    */
   filterSort?: (optionA: OptionsType[number], optionB: OptionsType[number]) => number;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value: ValueType) => void;

   /**
    * Callback called when the user is searching for a value
    *
    * @param value - The value to search
    */
   onSearch?: (value: string) => void;

   /**
    * Callback called when an option is selected
    * @param value - The value being deselected
    * @param option - The option object
    */
   onSelect?: (value: SingleType<ValueType>, option: OptionsType[number]) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Function called when the user presses a key.
    */
   onKeyDown?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Function called when the dirty state changes.  The input is considered dirty
    * when the raw string value that is currently selected does not match the last
    * good validated value.
    *
    * @param isDirty - True if the raw string value that is currently selected does not match the last good validated value
    */
   onDirtyStateChange?: (isDirty: boolean) => void;
}

const ENTER_KEY_CODE = 13;

const handleKeyDown = <ValueType extends DefaultValue = DefaultValue,>(props: SelectInputProps<ValueType>) => (event: React.KeyboardEvent<HTMLElement>) => {
   const { onPressEnter, onKeyDown } = props;

   if (event.keyCode === ENTER_KEY_CODE && onPressEnter) {
      onPressEnter(event);
   }

   if (onKeyDown) {
      onKeyDown(event);
   }
};

const createStyle = (width?: string | number, minWidth?: string | number, maxWidth?: string | number, style?: CSSProperties) => {
   return {
      width: convertSize(width, '250px'),
      minWidth: convertSize(minWidth, '60px'),
      maxWidth: convertSize(maxWidth),
      ...style
   };
};

export const SelectInput = forwardRef(function SelectInput<ValueType extends DefaultValue = DefaultValue>(props: React.PropsWithChildren<SelectInputProps<ValueType>>, ref: Ref<SelectRefProps<ValueType>>) {

   const selectRef = useRef<SelectRefProps<ValueType> | null>(null);
   const valueRef = useRef<ValueType | undefined>(props.value);
   const showBorder = props.border === undefined ? true : props.border;
   const padding = props.padding === undefined ? true : props.padding;
   const classNames = ['uf-select-input uf-input'];
   const { onBlur, onFocus, onChange, width, minWidth, maxWidth, style: styleProp } = props;

   const style = createStyle(width, minWidth, maxWidth, styleProp);

   if (showBorder) {
      classNames.push('uf-input-bordered');
   }

   if (props.className) {
      classNames.push(props.className);
   }

   if (props.disabled) {
      classNames.push('uf-input-disabled');
   }

   useEffect(()=>{
      valueRef.current=props.value;
   }, [props.value]);

   const onChangeHandler = useCallback((value: ValueType, option: OptionsType[number] | OptionsType) => {
      valueRef.current = value;
      if (onChange) {
         onChange(value);
      }
   }, [onChange]);

   const onBlurHandler = useCallback((event: FocusEvent<HTMLElement>) => {
      if (onBlur) {
         onBlur(event);
      }
   }, [onBlur]);

   const onFocusHandler = useCallback((event: FocusEvent<HTMLElement>) => {
      if (onFocus) {
         onFocus(event);
      }
   },[onFocus]);

   useImperativeHandle(ref, () => ({
      focus: () => {
         if (selectRef.current) {
            selectRef.current.focus();
         }
      },
      blur: () => {
         if (selectRef.current) {
            selectRef.current.blur();
         }
      },
   } as SelectRefProps<ValueType>));

   const formContext = useContext(FormContext);
   const inputId = generateFormFieldId(formContext.name, props.name);

   return (
      <span
         className={classNames.join(' ')}
         style={style}
      >
         <span className={'uf-input-wrapper'}>
            {props.addonBefore && <span className={'uf-input-addon'}>
               {props.addonBefore}
            </span>}

            <span className={`uf-input-control-container ${padding ? 'uf-select-input-control-container-padding' : ''}`}>

               <Select<ValueType>
                  id={inputId}
                  ref={selectRef}
                  bordered={false}
                  size={'middle'}
                  placeholder={props.placeholder}
                  disabled={props.disabled}
                  className={'uf-input-control'}
                  value={props.value}
                  autoFocus={props.autoFocus}
                  options={props.options}
                  listHeight={props.listHeight}
                  listItemHeight={props.listItemHeight}
                  dropdownStyle={props.dropdownStyle}
                  dropdownClassName={props.dropdownClassName}
                  dropdownMatchSelectWidth={props.dropdownMatchSelectWidth}
                  getPopupContainer={props.getPopupContainer}
                  loading={props.loading}
                  defaultActiveFirstOption={props.defaultActiveFirstOption}
                  notFoundContent={props.notFoundContent}
                  optionLabelProp={props.optionLabelProp}
                  optionFilterProp={props.optionFilterProp}
                  showSearch={props.showSearch}
                  filterOption={props.filterOption}
                  filterSort={props.filterSort}
                  onChange={onChangeHandler}
                  onBlur={onBlurHandler}
                  onFocus={onFocusHandler}
                  onSelect={props.onSelect}
                  onSearch={props.onSearch}
                  onKeyDown={handleKeyDown(props)}
                  mode={props.mode}
                  tokenSeparators={props.tokenSeparators}
                  tagRender={props.tagRender}
                  maxTagTextLength={props.maxTagTextLength}
                  maxTagPlaceholder={props.maxTagPlaceholder}
                  maxTagCount={props.maxTagCount}
               >
                  {props.children}
               </Select>
            </span>

            {props.addonAfter && <span className={'uf-input-addon'}>
               {props.addonAfter}
            </span>}
         </span>
      </span>
   );
}) as <ValueType extends DefaultValue = DefaultValue>(props: PropsWithChildren<SelectInputProps<ValueType>> & RefAttributes<SelectRefProps<ValueType>>) => ReactElement | null;
